<template>
  <div class="season-container main-space">
    <div class="color" :style="{ background: color }">
      <LegacyWrap>
        <div class="content">
          <div class="first-card">
            <h2 class="title" v-html="title" />
            <div class="subtitle" v-html="subtitle" />
          </div>
          <div class="slider">
            <UiSlider arrows-always-visible v-bind="cardProps">
              <template #skeleton>
                <SkeletonItem
                  :skeleton-style="{
                    width: '100%',
                    height: '380px',
                    borderRadius: '16px',
                  }"
                  :is-animated="true"
                />
              </template>
              <template #slide="{ slide, index }">
                <SeasonCard
                  v-view-tracker="getParams(slide, index)"
                  v-click-tracker="getParams(slide, index)"
                  :slide="slide"
                  :index="index"
                />
              </template>
              <template #rightArrowIcon>
                <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
              </template>
              <template #leftArrowIcon>
                <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
              </template>
            </UiSlider>
          </div>
        </div>
      </LegacyWrap>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { UiSlider } from '@experience/core-slider-ui';
import { computed } from 'vue';

import SeasonCard from '@/features/seasonContainer/SeasonCard.vue';
import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { useCarouselConfig } from '@/shared/lib/carousel/useCarouselConfig';
import { type SeasonSlide } from '@/shared/models/season';

const props = withDefaults(
  defineProps<{
    items: SeasonSlide[];
    isLoaded: boolean;
    title: string;
    subtitle: string;
    color?: string;
  }>(),
  {
    color: 'var(--alias-color-background-accent-blue-lightest)',
  },
);

const { useViewTracker, useImmediateClickTracker } = useAnalytics();

const vViewTracker = useViewTracker('main:todo-ideas:idea.view');
const vClickTracker = useImmediateClickTracker('main:todo-ideas:idea.click');

const slides = computed(() => props.items);

const getParams = (slide: SeasonSlide, index: number) => ({
  position: index,
  name: slide.slug,
  url_to: slide.url + (slide.query ? `?${slide.query}` : ''),
});

const cardProps = useCarouselConfig<SeasonSlide>({
  slides,
  mobileMini: {
    cardWidth: 300,
    spaceBetween: 16,
    options: { slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
  },
  tabletMobile: {
    slidesPerView: 2.3,
    spaceBetween: 16,
    options: { slidesOffsetBefore: 20, slidesOffsetAfter: 20, breakpoints: { 767: { slidesPerGroup: 2 } } },
  },
  desktop: {
    slidesPerView: 3,
    spaceBetween: 8,
    options: { breakpoints: { 1023: { slidesPerGroup: 3 } } },
  },
});
</script>

<style lang="scss" scoped>
.season-container {
  width: 100%;
  max-width: 1348px;
  margin: 0 auto;
}

.slider {
  width: 100%;
  min-width: calc(var(--wrap-min-width) - 300px);
  max-width: calc(var(--wrap-max-width) - 300px);

  @include adaptivity.tablet {
    min-width: 100%;
    max-width: 100%;
    padding: 0;
    margin: var(--alias-space-24, 24px) auto;
  }

  @include adaptivity.mobile {
    min-width: var(--wrap-min-width);
    max-width: var(--wrap-max-width) - 300px;
    margin: var(--alias-space-20, 20px) auto 0;
  }
}

.mirror {
  transform: scaleX(-1);
}

.first-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 33%;
  max-width: 300px;
  margin-right: 8px;

  @include adaptivity.tablet {
    width: 100%;
    max-width: 100%;
    padding: 0 20px;
  }
}

.title {
  color: var(--alias-color-text-default);

  @include typography.base-800-750-extra;

  @include adaptivity.tablet {
    text-align: left;
  }

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.subtitle {
  margin-top: 16px;

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    margin-top: 12px;
  }
}

.color {
  width: 100%;
  padding: var(--alias-space-48, 48px) 0 var(--alias-space-56, 56px);
  border-radius: 32px;

  @include adaptivity.tablet {
    padding: 40px 0;
    border-radius: 0;
  }

  @include adaptivity.mobile {
    padding: 32px 0;
  }
}

.content {
  display: flex;

  @include adaptivity.tablet {
    flex-direction: column;
  }
}

.slider:hover {
  :deep(.btn-prev) {
    opacity: 1;
  }

  :deep(.btn-next) {
    opacity: 1;
  }
}

:deep(.btn-prev) {
  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.btn-next) {
  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.wrap) {
  @include adaptivity.tablet {
    padding: 0;
  }
}
</style>
